/**
 * Times as people say them: a length is "42 min" or "1 h 05 min", never seconds; a moment in a
 * video is "12:30". Durations are read from the video, not typed (section 4). Pure.
 */

/** "42 min", "1 h 05 min", "45 s". */
export function lengthWords(seconds: number | undefined | null): string {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return "";
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")} min`;
}

/** 754 → "12:34", 3754 → "1:02:34". */
export function clock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
  return `${h ? `${h}:` : ""}${String(m).padStart(h ? 2 : 1, "0")}:${String(s).padStart(2, "0")}`;
}

/** "12:34" → 754, "1:02:34" → 3754, "90" → 90, "1.5" → 1.5; null when it is not a time. */
export function parseClock(text: string): number | null {
  const t = text.trim();
  if (!t) return null;
  if (/^\d+(\.\d+)?$/.test(t)) return Number(t);
  const m = t.match(/^(?:(\d+):)?(\d{1,2}):(\d{1,2}(?:\.\d+)?)$/);
  if (!m) return null;
  const [, h, min, s] = m;
  if (Number(s) >= 60 || (h !== undefined && Number(min) >= 60)) return null;
  return Number(h ?? 0) * 3600 + Number(min) * 60 + Number(s);
}

/** An ISO time as the value of a date-time input, in the editor's own zone. */
export function toLocalInput(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A date-time input's value back to an absolute ISO time (with Z), or undefined when empty. */
export function fromLocalInput(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** The year of an ISO date ("2010-09-27T00:00:00Z" → "2010"). */
export function yearOf(iso: unknown): string {
  return typeof iso === "string" && /^\d{4}/.test(iso) ? iso.slice(0, 4) : "";
}

/** A year to the ISO date the feed keeps (1 January, UTC), keeping month and day when one was there. */
export function withYear(iso: unknown, year: string): string | undefined {
  if (!/^\d{4}$/.test(year)) return undefined;
  if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}T/.test(iso)) return `${year}${iso.slice(4)}`;
  return `${year}-01-01T00:00:00Z`;
}

/** The date part of an ISO time ("2026-10-22"), for a date input. */
export function dateOf(iso: unknown): string {
  return typeof iso === "string" && /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : "";
}

/** A date input's value to the feed's ISO time (midday UTC, so no zone shifts the day). */
export function fromDate(value: string): string | undefined {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : undefined;
}
